import React, { useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Upload, Loader2, CheckCircle2, AlertTriangle, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

export const ImportCustomersDialog = ({ open, onOpenChange, onImported }) => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const close = (v) => { onOpenChange(v); if (!v) { setFile(null); setResult(null); } };

  const upload = async () => {
    if (!file) { toast.error("Elige un archivo CSV"); return; }
    setLoading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/customers/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(res.data);
      if (res.data.created > 0) onImported?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo importar el archivo");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="import-customers-dialog" className="max-w-md rounded-2xl">
        <DialogHeader><DialogTitle className="font-display">Importar clientes desde CSV</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="rounded-xl bg-[#E6F7FE] px-4 py-3 text-xs text-neutral-600">
            El archivo debe tener las columnas <b>nombre</b>, <b>correo</b> y <b>teléfono</b>. Los correos que ya existan se omiten.
          </div>
          <label data-testid="csv-file-label" className="flex items-center gap-2 cursor-pointer rounded-xl border border-dashed border-neutral-300 px-3.5 py-3 text-sm text-neutral-500 hover:border-[#00AEEF] hover:text-[#0092CC] transition-colors">
            <FileSpreadsheet size={16} />
            {file ? file.name : "Elegir archivo .csv"}
            <input data-testid="csv-file-input" type="file" accept=".csv,text/csv" className="hidden"
              onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }} />
          </label>

          {result && (
            <div className="space-y-2" data-testid="import-result">
              <div className="flex items-center gap-2 text-sm text-emerald-700">
                <CheckCircle2 size={15} /> <b>{result.created}</b> cliente(s) creado(s) · <b>{result.skipped}</b> omitido(s) por correo repetido
              </div>
              {result.errors?.length > 0 && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
                  <p className="flex items-center gap-1.5 text-xs font-semibold text-amber-700 mb-1.5">
                    <AlertTriangle size={13} /> {result.errors.length} fila(s) con error
                  </p>
                  <ul className="text-xs text-amber-700 space-y-0.5 max-h-40 overflow-y-auto">
                    {result.errors.map((er, i) => <li key={i}>Fila {er.row}: {er.reason}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => close(false)} className="rounded-xl">{result ? "Cerrar" : "Cancelar"}</Button>
          <Button data-testid="import-csv-submit" onClick={upload} disabled={loading || !file}
            className="rounded-xl gap-2 bg-[#00AEEF] hover:bg-[#0092CC] text-white">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} Importar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
